import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { Button, Col, DatePicker, Flex, Form, Input, Modal, Row, Select, Switch, Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import React, { useEffect, useRef, useState } from "react";
import FormattedNumberInput from "../../../components/format-number-input";
import { BOOKING_NO_PATTERN, DATE_FORMAT } from "../../../constants/common";
import { SupplierInvoiceRecordStatusEnum } from "../../../constants/enum";
import { useMessageApi } from "../../../context/MessageContext";
import { ISirTable, ISirTableData } from "../../../interfaces/invoice";
import { ISuppliersByNameResult } from "../../../interfaces/supplier";
import { InvoiceServices } from "../../../services/invoice";
import { roundCost } from "../../../utils/helper";

const defaultRow = {
  status: "",
  bookingNo: undefined,
  amount: undefined,
  leadPax: undefined,
  isActive: true,
  reason: "",
};

interface CreateInvoiceProps {
  isVisible: boolean;
  onClose: (okTriggered?: boolean) => void;
  suppliers: ISuppliersByNameResult[];
  isFlight?: boolean;
  isPermissionEdit?: boolean;
}

const CreateInvoice: React.FC<CreateInvoiceProps> = ({
  isVisible,
  onClose,
  suppliers,
  isFlight = false,
  isPermissionEdit,
}) => {
  console.log(1111);
  const [isLoadingSubmit, setIsLoadingSubmit] = useState<boolean>(false);
  const messageApi = useMessageApi();
  const [form] = Form.useForm();
  const supplierInvoiceRecords = Form.useWatch("supplierInvoiceRecords", form);

  const tableRef = useRef<HTMLDivElement>(null);
  const [bookingFormatError, setBookingFormatError] = useState<string | null>(null);

  useEffect(() => {
    form.resetFields();
    form.setFieldsValue({
      receiveDate: dayjs(),
      invoiceDate: dayjs(),
      supplierInvoiceRecords: [{ ...defaultRow }],
    });
  }, [isVisible, form, isFlight]);

  const onFinish = async (values: ISirTableData) => {
    try {
      const sirTotalAmount = roundCost(
        (values.supplierInvoiceRecords || [])
          .filter((r: ISirTable) => r.isActive)
          .reduce((sum: number, item: any) => sum + (item.amount || 0), 0)
      );
      const totalAmount = roundCost(values?.totalAmount || 0);

      if (totalAmount && totalAmount !== sirTotalAmount) {
        messageApi.error(
          `Cost mismatched between SIR and Invoice, the remaining cost is ${(totalAmount - sirTotalAmount).toFixed(2)}`
        );
        return;
      }

      const isValidAmountCreditNote = (values.supplierInvoiceRecords || []).some((sir: any) =>
        requiredAmountCreditNote(sir)
      );

      if (isValidAmountCreditNote) {
        messageApi.error(`Credit note must be negative amount`);
        return;
      }

      setIsLoadingSubmit(true);
      const body = {
        ...values,
        receiveDate: dayjs(values.receiveDate),
        invoiceDate: dayjs(values.invoiceDate),
        supplierInvoiceRecords: supplierInvoiceRecords.map((item: ISirTable) => ({
          ...item,
          reason: item?.reason || "",
        })),
      };

      await InvoiceServices.createInvoice(body);

      messageApi.success(`Create Invoice successfully!`);
      onClose(true);
    } catch (err: any) {
      const message = err.message ?? `Create Invoice failed!`;
      messageApi.error(message);
    } finally {
      setIsLoadingSubmit(false);
    }
  };

  const onFinishFailed = (errorInfo: any) => {
    const isValidFormatBookingNo = errorInfo?.values?.supplierInvoiceRecords.some((sir: any) =>
      validFormatBooking(sir)
    );
    if (isValidFormatBookingNo) {
      setBookingFormatError("Invalid Booking Reference ID");
    }
  };

  const filterSelectOption = (input: string, option?: { label: string; name: string }) => {
    const label = option?.label?.toString().toLowerCase() ?? "";
    const name = option?.name?.toLowerCase?.() ?? "";

    return label.includes(input.toLowerCase()) || name.includes(input.toLowerCase());
  };

  const optionRender = (option: any) => <>{`${option.data.label} - ${option.data.name}`}</>;

  const handleChangeSupplierCode = (value: string) => {
    const find = suppliers.find((item) => item.key === value);
    form.setFieldValue("supplierName", find?.label);
    form.setFieldValue("currency", find?.currency);
  };

  const requiredBookingOrLeadPax = (row: ISirTable) => {
    const { bookingNo, leadPax } = row || {};
    if (!isCheckDisabledRow(row) && !bookingNo && !leadPax) {
      return true;
    }
    return false;
  };

  const validFormatBooking = (row: ISirTable) => {
    const { bookingNo } = row || {};
    if (!isCheckDisabledRow(row)) {
      if (bookingNo && !BOOKING_NO_PATTERN.test(bookingNo)) {
        return true;
      }
    }
    return false;
  };

  const requiredAmountCreditNote = (row: ISirTable) => {
    const { invoiceMatchId, amount } = row || {};
    if (!isCheckDisabledRow(row) && invoiceMatchId && Number(amount) >= 0) {
      return true;
    }
    return false;
  };

  const isCheckDisabledRow = (row: ISirTable) => {
    const { isActive, status } = row || {};
    if (!isActive || status === SupplierInvoiceRecordStatusEnum.EXPORTED) {
      return true;
    }
    return false;
  };

  const isCredit = (index: number) => {
    return !!supplierInvoiceRecords?.[index]?.invoiceMatchId;
  };

  return (
    <Modal open={isVisible} onCancel={() => onClose()} title="Create Invoice - SIR" footer={null} width={1040}>
      <Form
        form={form}
        layout="horizontal"
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        disabled={!isPermissionEdit}
      >
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              label="Supplier Code"
              name="supplierId"
              labelCol={{ span: 9 }}
              rules={[{ required: true, message: "Complete this required field." }]}
            >
              <Select
                showSearch
                allowClear
                filterOption={filterSelectOption}
                options={suppliers.map((item) => ({
                  value: item.key,
                  label: item.code,
                  name: item.label,
                }))}
                placeholder="Select"
                optionRender={optionRender}
                onChange={handleChangeSupplierCode}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Supplier Name" name="supplierName" labelCol={{ span: 9 }}>
              <Input disabled />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Invoice Number"
              name="invoiceNumber"
              labelCol={{ span: 9 }}
              rules={[{ required: isFlight, message: "Complete this required field." }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Receive Date"
              name="receiveDate"
              labelCol={{ span: 9 }}
              rules={[{ required: true, message: "Complete this required field." }]}
            >
              <DatePicker className="w-full" format={DATE_FORMAT} placeholder="Select date" disabled />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Currency" name="currency" labelCol={{ span: 9 }}>
              <Input readOnly disabled={true} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Invoice Date"
              name="invoiceDate"
              labelCol={{ span: 9 }}
              rules={[{ required: true, message: "Complete this required field." }]}
            >
              <DatePicker className="w-full" format={DATE_FORMAT} placeholder="Select date" disabled />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item label="Total Invoice Amount" name="totalAmount" labelCol={{ span: 9 }}>
              <FormattedNumberInput className="w-full" disabled />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item shouldUpdate noStyle>
          {({ getFieldValue }) => {
            const sirTables = getFieldValue(["supplierInvoiceRecords"]) || [];
            const isValidBookingOrLeadPax = sirTables.some((sir: any) => requiredBookingOrLeadPax(sir));

            return (
              <div className="text-error" style={{ textAlign: "center", margin: "10px 0" }}>
                {isValidBookingOrLeadPax && (
                  <div>
                    Please fill out the <strong>Booking Reference ID</strong> or <strong>Lead Passenger</strong> fields
                    before saving the invoice record
                  </div>
                )}
              </div>
            );
          }}
        </Form.Item>

        {bookingFormatError && (
          <div className="text-error" style={{ textAlign: "center", margin: "10px 0" }}>
            {bookingFormatError}
          </div>
        )}

        <Form.List name="supplierInvoiceRecords">
          {(fields, { add }) => {
            const columns: ColumnsType<any> = [
              {
                title: "No.",
                dataIndex: "no",
                width: 45,
                render: (_: any, __: any, index: number) => index + 1,
              },
              {
                title: "Booking Reference ID",
                dataIndex: "bookingNo",
                width: 180,
                render: (_: any, record: any) => (
                  <Form.Item
                    name={[record.name, "bookingNo"]}
                    rules={[
                      {
                        required: isFlight ? true : requiredBookingOrLeadPax(supplierInvoiceRecords?.[record.name]),
                        message: "",
                      },
                      ...(isCredit(record.name)
                        ? []
                        : [
                            {
                              pattern: BOOKING_NO_PATTERN,
                              message: "",
                              validateTrigger: "onBlur",
                            },
                          ]),
                      {
                        validator: (_, value) => {
                          if (value && !value.trim()) {
                            return Promise.reject(new Error(""));
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Input
                      placeholder="Enter booking ref"
                      disabled={
                        !isPermissionEdit ||
                        isCheckDisabledRow(supplierInvoiceRecords?.[record.name]) ||
                        isCredit(record.name)
                      }
                      onChange={(e) => {
                        form.validateFields([["supplierInvoiceRecords", record.name, "leadPax"]]);
                        setBookingFormatError(null);
                      }}
                    />
                  </Form.Item>
                ),
              },
              {
                title: "Lead Passenger",
                dataIndex: "leadPax",
                width: 180,
                render: (_: any, record: any) => (
                  <Form.Item
                    name={[record.name, "leadPax"]}
                    rules={[
                      { required: requiredBookingOrLeadPax(supplierInvoiceRecords?.[record.name]), message: "" },
                      {
                        validator: (_, value) => {
                          if (value && !value.trim()) {
                            return Promise.reject(new Error(""));
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Input
                      placeholder="Enter Lead Passenger"
                      disabled={
                        !isPermissionEdit ||
                        isCheckDisabledRow(supplierInvoiceRecords?.[record.name]) ||
                        isCredit(record.name)
                      }
                      onChange={(e) => {
                        form.validateFields([["supplierInvoiceRecords", record.name, "bookingNo"]]);
                      }}
                    />
                  </Form.Item>
                ),
              },
              {
                title: "Amount",
                dataIndex: "amount",
                width: 140,
                render: (_: any, record: any) => (
                  <Form.Item
                    name={[record.name, "amount"]}
                    rules={[{ required: supplierInvoiceRecords?.[record.name]?.isActive, message: "" }]}
                  >
                    <FormattedNumberInput
                      className="w-full input-t-r"
                      placeholder="Enter amount"
                      disabled={!isPermissionEdit || isCheckDisabledRow(supplierInvoiceRecords?.[record.name])}
                      onChange={(value) => {
                        form.setFieldValue("totalAmount", value);
                      }}
                      {...(isCredit(record.name) &&
                        Number(supplierInvoiceRecords?.[record.name]?.amount) >= 0 && {
                          status: "error",
                        })}
                    />
                  </Form.Item>
                ),
              },
              {
                title: "Active",
                dataIndex: "isActive",
                width: 70,
                align: "center" as const,
                render: (_: any, record: any) => {
                  return (
                    <Form.Item
                      name={[record.name, "isActive"]}
                      valuePropName="checked"
                      style={{ display: "inline-block" }}
                    >
                      <Switch checkedChildren={<CheckOutlined />} unCheckedChildren={<CloseOutlined />} disabled />
                    </Form.Item>
                  );
                },
              },
              {
                title: "Reason",
                dataIndex: "reason",
                width: 200,
              },
            ];

            return (
              <div ref={tableRef}>
                <Table
                  dataSource={fields}
                  pagination={false}
                  rowKey={(f) => f.key}
                  columns={columns}
                  size="small"
                  className="table-fm"
                  scroll={{ y: 50 * 4 }}
                  rowClassName={(record) => {
                    const classes = [];
                    if (isCredit(record.name)) classes.push("active-credit");

                    return classes.join(" ");
                  }}
                />
              </div>
            );
          }}
        </Form.List>

        <Flex justify="end" gap={16} style={{ marginTop: 20 }}>
          <Button
            style={{ marginLeft: 8 }}
            onClick={() => onClose()}
            disabled={isLoadingSubmit}
            color="primary"
            variant="outlined"
          >
            Cancel
          </Button>
          {isPermissionEdit && (
            <Button type="primary" htmlType="submit" loading={isLoadingSubmit}>
              Save
            </Button>
          )}
        </Flex>
      </Form>
    </Modal>
  );
};

export default CreateInvoice;
